import React, { useState, useCallback, useMemo, forwardRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import PropertyRefundabilitySelector from './PropertyRefundabilitySelector'
import PropertyRateOption from './PropertyRateOption'
import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import OfferPagePackageInclusionsList from 'components/OfferPage/OfferPagePackageInclusionsList'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import Group from 'components/utils/Group'
import AccommodationPackageTileDetails from 'components/AccommodationKit/AccommodationPackageTileDetails'

const MergedContainer = styled.div`
  position: relative;
  padding: ${rem(20)};
  display: grid;
  grid-template-columns: 3fr 2fr;
  column-gap: ${rem(32)};
`

interface Props {
  lpcOffer?: App.HotelOffer
  rates: Array<App.PropertyRate>
  optionNumber: number
  showOptionLabel: boolean
  roomListId: string
  roomPosition: number
  rateListId: string
  propertyTimezone: string
}

/**
 * Merged variant that combines property rates with radio button selection.
 *
 * New UI structure:
 * - Radio selector for rates (by rate ID)
 * - Inclusions list
 * - Pricing and CTA buttons (reusing PropertyRateOption)
 */
const PropertyRateOptionMerged = forwardRef<HTMLDivElement, Props>(function PropertyRateOptionMerged(props, ref) {
  const {
    lpcOffer,
    rates,
    optionNumber,
    showOptionLabel,
    roomListId,
    roomPosition,
    rateListId,
    propertyTimezone,
  } = props

  // Default to first rate in the group
  const defaultRateId = rates[0]?.rateId
  const [selectedRateId, setSelectedRateId] = useState<string | undefined>(defaultRateId)

  const activeRate = useMemo(() => {
    return rates.find(rate => rate.rateId === selectedRateId) || rates[0]
  }, [rates, selectedRateId])

  const handleRateChange = useCallback((rateId: string) => {
    setSelectedRateId(rateId)
  }, [])

  const hasChoice = rates.length > 1

  const headlineCancellationPolicyStart = useMemo(
    () => activeRate?.headlineCancellationPolicyStart ? new Date(activeRate.headlineCancellationPolicyStart) : undefined,
    [activeRate?.headlineCancellationPolicyStart],
  )

  const cancellationPolicy = useMemo(() => {
    if (!activeRate) return null

    return (
      <PropertyCancellationPolicy
        propertyTimeZone={propertyTimezone}
        headlineCancellationPolicyType={activeRate.headlineCancellationPolicyType}
        headlineCancellationPolicyStart={headlineCancellationPolicyStart}
        fullyRefundableUntil={activeRate.fullyRefundableUntil}
        cancellationPolicies={activeRate.cancellationPolicies}
        currency={activeRate.currency}
        shouldClarifyRefundIsHotelOnly={activeRate.includesFlights}
      />
    )
  }, [
    activeRate,
    propertyTimezone,
    headlineCancellationPolicyStart,
  ])

  if (!activeRate) {
    return null
  }

  const packageNumber = showOptionLabel ? optionNumber : undefined

  return (
    <MergedContainer ref={ref} data-testid="merged-property-rate-option">
      <VerticalSpacer gap={hasChoice ? 20 : 12}>
        {packageNumber != null && <AccommodationPackageTileDetails
          packageNumber={packageNumber}
        />}

        {hasChoice && (
          <PropertyRefundabilitySelector
            rates={rates}
            selectedRateId={selectedRateId}
            onChange={handleRateChange}
            propertyTimezone={propertyTimezone}
          />
        )}

        {!hasChoice && (
          <Group direction="vertical" gap={4} noDisplayWhenEmpty>
            {cancellationPolicy}
          </Group>
        )}

        <LargeContentToggle
          maxHeight={208}
          showMoreText="View more inclusions"
          showLessText="View less inclusions"
        >
          <VerticalSpacer gap={12}>
            {activeRate.luxPlusInclusions.length > 0 && <LuxPlusInclusions
              luxPlusInclusions={activeRate.luxPlusInclusions}
              accommodationOffer={lpcOffer}
            />}
            {activeRate.bonusInclusions.length > 0 && <OfferPagePackageInclusionsList title="bonus">
              {activeRate.bonusInclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
            </OfferPagePackageInclusionsList>}
            {activeRate.inclusions.length > 0 && <VerticalSpacer gap={4}>
              {activeRate.inclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
            </VerticalSpacer>}
          </VerticalSpacer>
        </LargeContentToggle>
      </VerticalSpacer>

      <PropertyRateOption
        lpcOffer={lpcOffer}
        rate={activeRate}
        optionNumber={activeRate.defaultOrder + 1}
        showOptionLabel={false}
        roomListId={roomListId}
        roomPosition={roomPosition}
        rateListId={rateListId}
      />
    </MergedContainer>
  )
})

PropertyRateOptionMerged.displayName = 'PropertyRateOptionMerged'

export default PropertyRateOptionMerged
